import React, {useState} from "react";
import styles, {C} from "../styles";
import {validatePassword} from "../utils";
import {apiChangePassword, apiGetPseudoSuggestions, apiUpdateProfile} from "../api/auth";
import {useToast} from "../components/FeedbackProvider";

export default function ProfilePage({user, onUserUpdate}) {
    const {notify} = useToast();
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newRecoveryEmail, setNewRecoveryEmail] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedPseudo, setSelectedPseudo] = useState(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const isAdmin = user?.role === "admin";
    const roleLabel = isAdmin ? "Administrateur SAPPM" : user?.role === "agent_sappm" ? "Agent SAPPM" : "Agent";
    const pwdCheck = validatePassword(newPassword);

    function loadSuggestions() {
        setLoadingSuggestions(true);
        setSelectedPseudo(null);
        apiGetPseudoSuggestions().then(data => {
            setSuggestions(Array.isArray(data) ? data : []);
            setLoadingSuggestions(false);
        });
    }

    async function changePassword() {
        const data = await apiChangePassword(oldPassword, newPassword);
        if (data.error) {
            notify(data.error, "error");
            return;
        }
        notify("Mot de passe modifié avec succès.", "success");
        setOldPassword("");
        setNewPassword("");
    }

    async function updateUsername() {
        if (!selectedPseudo) return;
        const data = await apiUpdateProfile(selectedPseudo.pseudo, user.email, user.recovery_email);
        if (data.error) {
            notify(data.error, "error");
            return;
        }
        notify("Pseudo modifié", "success");
        onUserUpdate({...user, username: selectedPseudo.pseudo});
        setSuggestions([]);
        setSelectedPseudo(null);
    }

    async function updateEmail() {
        const data = await apiUpdateProfile(user.username, newEmail, user.recovery_email);
        if (data.error) {
            notify(data.error, "error");
            return;
        }
        // Le nouvel email pro n'est actif qu'après confirmation via le lien reçu : l'adresse
        // affichée reste l'ancienne d'ici là.
        notify(data.message, "success");
        setNewEmail("");
    }

    async function updateRecoveryEmail() {
        const data = await apiUpdateProfile(user.username, user.email, newRecoveryEmail);
        if (data.error) {
            notify(data.error, "error");
            return;
        }
        notify("Email de secours mis à jour", "success");
        onUserUpdate({...user, recovery_email: data.user.recovery_email});
        setNewRecoveryEmail("");
    }

    return (
        <div style={styles.card}>
            <h2 style={{fontFamily: "'Fraunces', serif"}}>👤 Mon profil</h2>
            <p><strong>Pseudo :</strong> {user?.username}</p>
            <p><strong>📧 Email :</strong> {user?.email || "Non renseigné"}</p>
            <p><strong>Rôle :</strong> {roleLabel}</p>
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>✏️ Modifier mon pseudo</h3>
            {suggestions.length === 0 ? (
                <button style={styles.button} onClick={loadSuggestions} disabled={loadingSuggestions}>
                    {loadingSuggestions ? "Génération des suggestions…" : "Choisir un nouveau pseudo"}
                </button>
            ) : (
                <>
                    <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 8px"}}>
                        Choisissez votre nouveau pseudo — il préserve votre anonymat
                    </p>
                    <div style={{display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap"}}>
                        {suggestions.map(s => (
                            <button
                                key={s.pseudo}
                                type="button"
                                onClick={() => setSelectedPseudo(s)}
                                style={{
                                    flex: "1 1 120px",
                                    padding: "12px 8px",
                                    borderRadius: 12,
                                    border: selectedPseudo?.pseudo === s.pseudo
                                        ? `2px solid ${C.primary}`
                                        : `1px solid ${C.border}`,
                                    background: `radial-gradient(circle at 35% 30%, ${s.couleur}CC, ${s.couleur}, ${s.couleur}88)`,
                                    color: "white",
                                    cursor: "pointer",
                                    textAlign: "center",
                                    transform: selectedPseudo?.pseudo === s.pseudo ? "scale(1.04)" : "scale(1)",
                                    transition: "transform 0.15s, border-color 0.15s",
                                    minHeight: "auto",
                                    boxShadow: selectedPseudo?.pseudo === s.pseudo
                                        ? `0 0 0 3px ${C.primaryLight}`
                                        : "0 2px 6px rgba(0,0,0,0.12)",
                                }}
                            >
                                <div style={{fontSize: 28, marginBottom: 4}}>{s.emoji}</div>
                                <div style={{fontSize: 13, fontWeight: 700, textShadow: "0 1px 2px rgba(0,0,0,0.3)"}}>{s.pseudo}</div>
                            </button>
                        ))}
                    </div>
                    <button type="button" onClick={loadSuggestions} style={{...styles.button, fontSize: 12, marginBottom: 12}}>
                        🔄 Autres suggestions
                    </button>
                    <br/>
                    <button style={styles.button} onClick={updateUsername} disabled={!selectedPseudo}>
                        Modifier mon pseudo
                    </button>
                </>
            )}
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>📧 Modifier mon email</h3>
            <p style={{fontSize: 13, color: "#5E7472"}}>
                La nouvelle adresse doit être une adresse professionnelle. Un lien de confirmation y sera
                envoyé : votre adresse actuelle reste active tant que vous n'avez pas cliqué dessus.
            </p>
            <input type="email" placeholder="Nouvel email professionnel" value={newEmail}
                   onChange={e => setNewEmail(e.target.value)} style={styles.input}/>
            <button style={styles.button} onClick={updateEmail}>Modifier mon email</button>
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>📧✉️ Email de secours (optionnel)</h3>
            <p style={{fontSize: 13, color: "#5E7472"}}>
                Utilisé pour recevoir vos emails de récupération si votre adresse
                professionnelle ne peut pas les recevoir.
            </p>
            <p><strong>Actuel :</strong> {user?.recovery_email || "Non renseigné"}</p>
            <input type="email" placeholder="Email de secours (laisser vide pour retirer)" value={newRecoveryEmail}
                   onChange={e => setNewRecoveryEmail(e.target.value)} style={styles.input}/>
            <button style={styles.button} onClick={updateRecoveryEmail}>Enregistrer l'email de secours</button>
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>🔑 Modifier mon mot de passe</h3>
            <input type="password" placeholder="Ancien mot de passe" value={oldPassword}
                   onChange={e => setOldPassword(e.target.value)} style={styles.input}/>
            <input type="password" placeholder="Nouveau mot de passe" value={newPassword}
                   onChange={e => setNewPassword(e.target.value)} style={styles.input}/>
            {newPassword.length > 0 && (
                <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px", fontSize: 12, marginBottom: 10}}>
                    {[
                        [pwdCheck.minLength, "8 caractères min."],
                        [pwdCheck.hasUpper, "1 majuscule"],
                        [pwdCheck.hasDigit, "1 chiffre"],
                        [pwdCheck.hasSpecial, "1 caractère spécial"],
                    ].map(([ok, label]) => (
                        <span key={label} style={{color: ok ? "#22C55E" : C.textMuted}}>
                            {ok ? "✅" : "○"} {label}
                        </span>
                    ))}
                </div>
            )}
            <button onClick={changePassword} style={styles.button} disabled={!pwdCheck.valid}>
                Modifier mon mot de passe
            </button>
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>📩 Nous contacter</h3>
            <button style={styles.button} onClick={() => {
                window.location.href = "mailto:sappm@pm.gouv.fr";
            }}>
                Contacter le SAPPM
            </button>
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>❓ FAQ</h3>
            <p>Cette plateforme permet aux agents d'exprimer anonymement leurs idées, ressentis et propositions.</p>
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>🛡️ Comment nous garantissons votre anonymat</h3>
            <ul>
                <li>Aucun résultat n'est affiché avant un seuil minimum de participation.</li>
                <li>Les publications sont séparées des identités réelles.</li>
                <li>Les administrateurs ne peuvent pas voir les votes individuels.</li>
            </ul>
            <hr/>

            <h3 style={{fontFamily: "'Fraunces', serif"}}>🏛️ Qui sommes-nous ?</h3>
            <p>Le SAPPM accompagne les agents des services du Premier ministre dans le dialogue social et l'amélioration
                des conditions de travail.</p>
        </div>
    );
}
